import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import styles from "./TicketmasterCatalog.module.css";
import { CircularProgress } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import ToasterWithMessage from "../../components/shared-components/toaster";
import {
  GET_TICKETMASTER_EVENTS,
  GET_TICKETMASTER_EVENT_GLOBAL_STATS,
  GET_TICKETMASTER_LEAGUES,
  GET_TICKETMASTER_ARTIST_NAMES,
  UPDATE_EVENT_STATUS,
  BULK_UPDATE_EVENT_STATUS, // New mutation for bulk updates
} from "./api/TicketmasterCatalogQuery";
import { buildTicketmasterEventsWhere } from "./buildTicketmasterEventsWhere";

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

const TicketmasterCatalog: React.FC = () => {
  const { error: showError, success } = ToasterWithMessage();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | "Active" | "Removal" | "postponed" | "offsale" | "date-tbd" | "on-sale" | "closed">("All");
  const [leagueFilter, setLeagueFilter] = useState("All Leagues");
  const [leagueSearch, setLeagueSearch] = useState("");
  const [artistsFilter, setArtistsFilter] = useState("All Artists");
  const [artistSearch, setArtistSearch] = useState("");
  const [genresFilter, setGenresFilter] = useState("All Genres");
  const [showLeagueSuggestions, setShowLeagueSuggestions] = useState(false);
  const [showArtistSuggestions, setShowArtistSuggestions] = useState(false);

  // Bulk selection state
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState<{ type: 'activate' | 'deactivate'; events: any[] } | null>(null);

  const eventWhere = useMemo(
    () =>
      buildTicketmasterEventsWhere({
        searchQuery,
        statusFilter,
        leagueFilter,
        artistsFilter,
        genresFilter,
      }),
    [searchQuery, statusFilter, leagueFilter, artistsFilter, genresFilter]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const listVariables = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
      where: eventWhere,
    }),
    [page, pageSize, eventWhere]
  );

  const { data, loading, refetch: refetchEvents, error } = useQuery(GET_TICKETMASTER_EVENTS, {
    variables: listVariables,
    fetchPolicy: "network-only",
  });

  const { data: statsData, error: statsError, refetch: refetchStats } = useQuery(GET_TICKETMASTER_EVENT_GLOBAL_STATS, {
    fetchPolicy: "cache-first",
  });

  const { data: leaguesData } = useQuery(GET_TICKETMASTER_LEAGUES, {
    fetchPolicy: "cache-first",
  });

  const artistLeagueName = leagueFilter !== "All Leagues" ? leagueFilter : "%";

  const { data: artistsData, refetch: refetchArtists } = useQuery(GET_TICKETMASTER_ARTIST_NAMES, {
    variables: { leagueName: artistLeagueName },
    fetchPolicy: "cache-first",
  });

  const [updateEventStatus] = useMutation(UPDATE_EVENT_STATUS, {
    onCompleted: () => {
      void refetchEvents();
      void refetchStats();
      void refetchArtists();
    },
  });

  // Bulk update mutation
  const [bulkUpdateEventStatus] = useMutation(BULK_UPDATE_EVENT_STATUS, {
    onCompleted: () => {
      void refetchEvents();
      void refetchStats();
      void refetchArtists();
      setSelectedEvents(new Set());
      setIsBulkMode(false);
    },
  });

  // State management
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [pendingConfirmEvent, setPendingConfirmEvent] = useState<any | null>(null);

  const events = data?.events || [];

  const normalizeText = (value?: string) => (value || "").trim().toLowerCase();

  const normalizeGenre = (value?: string) => {
    const normalized = (value || "").trim().toLowerCase();

    if (normalized.includes("sport")) return "Sports";
    if (normalized.includes("music") || normalized.includes("concert")) return "Music";
    if (
      normalized.includes("theater") ||
      normalized.includes("theatre") ||
      normalized.includes("arts") ||
      normalized.includes("comedy")
    ) {
      return "Theater";
    }

    return "Other";
  };

  const getNormalizedGenreFromEvent = (event: any) => {
    const candidates = [event?.genre, event?.sub_genre, event?.segment, event?.event_name];
    for (const value of candidates) {
      const mapped = normalizeGenre(value);
      if (mapped !== "Other") return mapped;
    }
    return "Other";
  };

  const getEventLeague = (event: any) => {
    const nestedLeague = event?.event_categories?.[0]?.league_category?.league?.l_name;
    return nestedLeague || event?.league || "Unknown";
  };

  const filteredTotal = data?.events_aggregate?.aggregate?.count ?? 0;

  const totalEvents = statsData?.total?.aggregate?.count ?? filteredTotal;
  const activeEvents =
    statsData?.active?.aggregate?.count ??
    events.filter((e: any) => isEventActiveStatus(e.status)).length;
  const deactivatedEvents =
    statsData?.deactivated?.aggregate?.count ??
    events.filter((e: any) => !isEventActiveStatus(e.status)).length;
  const sportEvents = events.filter((e: any) => getNormalizedGenreFromEvent(e) === "Sports").length;
  const musicEvents = events.filter((e: any) => getNormalizedGenreFromEvent(e) === "Music").length;
  const theaterEvents = events.filter((e: any) => getNormalizedGenreFromEvent(e) === "Theater").length;

  useEffect(() => {
    setPage(0);
    // Clear selections when filters change
    setSelectedEvents(new Set());
    setIsBulkMode(false);
  }, [searchQuery, statusFilter, leagueFilter, artistsFilter, genresFilter, pageSize]);

  useEffect(() => {
    if (filteredTotal === 0) return;
    const maxPage = Math.max(0, Math.ceil(filteredTotal / pageSize) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredTotal, pageSize, page]);

  useEffect(() => {
    if (error) {
      console.error("Error loading Ticketmaster events:", error.message, error.graphQLErrors);
    }
  }, [error]);

  useEffect(() => {
    if (statsError) {
      console.error("Error loading Ticketmaster stats:", statsError.message, statsError.graphQLErrors);
    }
  }, [statsError]);

  // Get unique values for dropdowns
  const uniqueLeagues = useMemo(() => {
    const eventLeagues = events
      .map((e: any) => getEventLeague(e))
      .filter((l: string) => l && l !== "Unknown");

    const dbLeagues = (leaguesData?.leagues || [])
      .map((league: { l_name?: string }) => league?.l_name || "")
      .filter((l: string) => l.trim().length > 0);

    const leagues = Array.from(new Set([...dbLeagues, ...eventLeagues]))
      .sort((a, b) => a.localeCompare(b)) as string[];

    return ["All Leagues", ...leagues];
  }, [events, leaguesData]);

  const uniqueArtists = useMemo<string[]>(() => {
    const names = ((artistsData?.events || []) as Array<{ artist?: string }>)
      .map((row) => (row?.artist || "").trim())
      .filter((a: string) => a && a !== "-" && a.toLowerCase() !== "unknown");
    const sorted = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)) as string[];
    return ["All Artists", ...sorted];
  }, [artistsData]);

  const displayedArtists = useMemo(() => {
    if (!artistSearch.trim()) return uniqueArtists;
    return uniqueArtists.filter((name) =>
      name.toLowerCase().includes(artistSearch.trim().toLowerCase())
    );
  }, [uniqueArtists, artistSearch]);

  const displayedLeagues = useMemo(() => {
    if (!leagueSearch.trim()) return uniqueLeagues;
    return uniqueLeagues.filter((name) =>
      name.toLowerCase().includes(leagueSearch.trim().toLowerCase())
    );
  }, [uniqueLeagues, leagueSearch]);

  useEffect(() => {
    if (leagueFilter !== "All Leagues" && artistsFilter !== "All Artists") {
      if (!uniqueArtists.includes(artistsFilter)) {
        setArtistsFilter("All Artists");
        setArtistSearch("");
      }
    }
  }, [leagueFilter, artistsFilter, uniqueArtists]);

  const uniqueGenres = useMemo(() => ["All Genres", "Music", "Sports", "Theater"], []);

  function isEventActiveStatus(status?: string) {
    const normalizedStatus = normalizeText(status);
    return normalizedStatus === "on-sale" || normalizedStatus === "onsale" || normalizedStatus === "active" || normalizedStatus === "active";
  }

  const handleRefresh = () => {
    void refetchEvents();
    void refetchStats();
    void refetchArtists();
    setSelectedEvents(new Set());
    setIsBulkMode(false);
  };

  // Bulk selection handlers
  const handleSelectEvent = (eventId: string, isSelected: boolean) => {
    setSelectedEvents(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(eventId);
      } else {
        newSet.delete(eventId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedEvents.size === events.length) {
      // Deselect all
      setSelectedEvents(new Set());
    } else {
      // Select all events on current page
      const allEventIds = events.map((e: any) => e.id);
      setSelectedEvents(new Set(allEventIds));
    }
  };

  const handleSelectAllPages = async () => {
    // Fetch all event IDs across all pages that match current filters
    try {
      const allEventsWhere = buildTicketmasterEventsWhere({
        searchQuery,
        statusFilter,
        leagueFilter,
        artistsFilter,
        genresFilter,
      });
      
      const { data: allEventsData } = await refetchEvents({
        limit: 10000, // Large limit to get all events
        offset: 0,
        where: allEventsWhere,
      });
      
      const allEventIds = allEventsData?.events?.map((e: any) => e.id) || [];
      setSelectedEvents(new Set(allEventIds));
      success(`Selected ${allEventIds.length} events across all pages`);
    } catch (err: any) {
      showError(`Error selecting all events: ${err.message || "Unknown error"}`);
    }
  };

  const clearSelection = () => {
    setSelectedEvents(new Set());
    setIsBulkMode(false);
  };

  // Bulk action handlers
  const handleBulkActivate = () => {
    const eventsToActivate = events.filter((e: any) => 
      selectedEvents.has(e.id) && !isEventActiveStatus(e.status)
    );
    
    if (eventsToActivate.length === 0) {
      showError("No deactivated events selected to activate");
      return;
    }
    
    setPendingBulkAction({
      type: 'activate',
      events: eventsToActivate
    });
  };

  const handleBulkDeactivate = () => {
    const eventsToDeactivate = events.filter((e: any) => 
      selectedEvents.has(e.id) && isEventActiveStatus(e.status)
    );
    
    if (eventsToDeactivate.length === 0) {
      showError("No active events selected to deactivate");
      return;
    }
    
    setPendingBulkAction({
      type: 'deactivate',
      events: eventsToDeactivate
    });
  };

  const confirmBulkAction = async () => {
    if (!pendingBulkAction) return;
    
    const { type, events: eventsToUpdate } = pendingBulkAction;
    const isActive = type === 'activate';
    const newStatus = isActive ? 'Active' : 'Removal';
    
    try {
      const eventIds = eventsToUpdate.map(e => e.id);
      const result = await bulkUpdateEventStatus({
        variables: {
          eventIds: eventIds,
          newStatus: newStatus,
          isActive: isActive,
        },
      });
      
      if (result.data?.update_events?.affected_rows > 0) {
        success(`${result.data.update_events.affected_rows} events ${type === 'activate' ? 'activated' : 'deactivated'} successfully`);
        setPendingBulkAction(null);
        setSelectedEvents(new Set());
        setIsBulkMode(false);
      } else {
        showError(`Failed to ${type} events`);
      }
    } catch (err: any) {
      showError(`Error updating events: ${err.message || "Unknown error"}`);
    }
  };

  const showInitialSpinner = loading && !data;
  const rangeStart = filteredTotal === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = filteredTotal === 0 ? 0 : Math.min((page + 1) * pageSize, filteredTotal);
  const totalPages = filteredTotal === 0 ? 1 : Math.ceil(filteredTotal / pageSize);
  const canPrev = page > 0;
  const canNext = filteredTotal > 0 && (page + 1) * pageSize < filteredTotal;

  const getEventStatusLabel = (status?: string) => {
    if (!status) return "Unknown";
    return isEventActiveStatus(status) ? "Active" : "Removal";
  };

  const getDisplayValue = (value: unknown) => {
    if (Array.isArray(value)) {
      const filteredValues = value
        .map((item) => (typeof item === "string" ? item.trim() : String(item ?? "").trim()))
        .filter((item) => item && item.toLowerCase() !== "null" && item.toLowerCase() !== "undefined");

      return filteredValues.length > 0 ? filteredValues.join(", ") : "-";
    }

    if (value === null || value === undefined) {
      return "-";
    }

    const normalizedValue = String(value).trim();
    if (!normalizedValue || normalizedValue.toLowerCase() === "null" || normalizedValue.toLowerCase() === "undefined") {
      return "-";
    }

    return normalizedValue;
  };

  const getDisplayClassName = (value: unknown, baseClassName?: string) => {
    const fallbackClassName = getDisplayValue(value) === "-" ? styles.emptyValue : "";
    return [baseClassName, fallbackClassName].filter(Boolean).join(" ");
  };

  const formatPriceRange = (value: unknown) => {
    const displayValue = getDisplayValue(value);
    if (displayValue === "-") {
      return "-";
    }

    const compactValue = displayValue.replace(/\s+/g, " ").trim();
    const hasCurrencyOrRange = /[$€£₹]|\bto\b|-|\d/.test(compactValue);
    const looksLikeAddress = /[a-zA-Z].*,\s*[a-zA-Z]/.test(compactValue) || /\b(stadium|arena|center|street|avenue|road|blvd|city)\b/i.test(compactValue);

    if (!hasCurrencyOrRange || looksLikeAddress) {
      return "-";
    }

    return compactValue;
  };

  const formatDateValue = (value: unknown) => {
    const displayValue = getDisplayValue(value);
    if (displayValue === "-") {
      return displayValue;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(displayValue)) {
      return displayValue;
    }

    const normalizedValue = displayValue.replace(/\./g, "/").trim();
    const plainDateMatch = normalizedValue.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
    if (plainDateMatch) {
      const [, year, month, day] = plainDateMatch;
      return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
    }

    const parsedDate = new Date(displayValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return displayValue;
    }

    const month = `${parsedDate.getMonth() + 1}`.padStart(2, "0");
    const day = `${parsedDate.getDate()}`.padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${month}/${day}/${year}`;
  };

  // Helper function to get league badge class
  const getLeagueBadgeClass = (league: string) => {
    const leagueUpper = league?.toUpperCase() || "";
    if (leagueUpper === "NBA") return styles.leagueNBA;
    if (leagueUpper === "NFL") return styles.leagueNFL;
    if (leagueUpper === "MLB") return styles.leagueMLB;
    if (leagueUpper === "NHL") return styles.leagueNHL;
    if (leagueUpper === "MLS") return styles.leagueMLS;
    if (leagueUpper === "WNBA") return styles.leagueWNBA;
    if (leagueUpper === "NCAAM" || leagueUpper === "NCAAW" || leagueUpper === "NCAAFB") return styles.leagueNCAA;
    if (leagueUpper === "CONCERT") return styles.leagueConcert;
    return styles.leagueDefault;
  };

  // Open confirmation dialog for activate/deactivate
  const handleDeactivateEvent = (event: any) => {
    setPendingConfirmEvent(event);
  } 

  // Open event details modal
  const handleOpenEventDetails = (event: any) => {
    setSelectedEvent(event);
    setOpenModal(true);
  } 

  const confirmStatusChange = async () => {
    const event = pendingConfirmEvent;
    if (!event) return;
    setPendingConfirmEvent(null);

    const currentStatus = event.status || "unknown";
    const isActive = isEventActiveStatus(currentStatus);
    const nextIsActive = !isActive;
    const newStatus = nextIsActive ? "Active" : "Removal";

    try {
      const result = await updateEventStatus({
        variables: {
          eventId: event.id,
          newStatus: newStatus,
          isActive: nextIsActive,
        },
      });

      if (result.data?.update_events?.affected_rows > 0) {
        success(`Event "${event.event_name}" has been ${isActive ? "deactivated" : "activated"} successfully`);
        if (selectedEvent && selectedEvent.id === event.id) {
          setSelectedEvent({ ...selectedEvent, status: newStatus });
        }
        setOpenModal(false);
      } else {
        showError("Failed to update event status");
      }
    } catch (err: any) {
      showError(`Error updating event: ${err.message || "Unknown error"}`);
    }
  };

  return (
    <div className={styles.container}>
      {/* BULK ACTION CONFIRMATION OVERLAY */}
      {pendingBulkAction && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmContainer}>
            <h3>{pendingBulkAction.type === 'activate' ? 'Activate Events' : 'Deactivate Events'}</h3>
            <p>
              Are you sure you want to <strong>{pendingBulkAction.type === 'activate' ? 'activate' : 'deactivate'}</strong>{" "}
              <strong>{pendingBulkAction.events.length}</strong> event{pendingBulkAction.events.length !== 1 ? 's' : ''}?
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.confirmCancelBtn} onClick={() => setPendingBulkAction(null)}>
                Cancel
              </button>
              <button
                className={pendingBulkAction.type === 'activate' ? styles.confirmActivateBtn : styles.confirmDeactivateBtn}
                onClick={confirmBulkAction}
              >
                Yes, {pendingBulkAction.type === 'activate' ? 'Activate' : 'Deactivate'} All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SINGLE EVENT CONFIRMATION OVERLAY */}
      {pendingConfirmEvent && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmContainer}>
            <h3>{isEventActiveStatus(pendingConfirmEvent.status) ? "Deactivate Event" : "Activate Event"}</h3>
            <p>
              Are you sure you want to{" "}
              <strong>{isEventActiveStatus(pendingConfirmEvent.status) ? "deactivate" : "activate"}</strong>{" "}
              &ldquo;{pendingConfirmEvent.event_name}&rdquo;?
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.confirmCancelBtn} onClick={() => setPendingConfirmEvent(null)}>
                Cancel
              </button>
              <button
                className={isEventActiveStatus(pendingConfirmEvent.status) ? styles.confirmDeactivateBtn : styles.confirmActivateBtn}
                onClick={confirmStatusChange}
              >
                Yes, {isEventActiveStatus(pendingConfirmEvent.status) ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* HEADER */}
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>
            Manage Ticketmaster Events - Activate or Deactivate for App Display
          </p>
          <h1 className={styles.title}>Ticketmaster Catalog</h1>
        </div>
        <div className={styles.searchSection}>
          <div className={styles.searchField}>
            <input
              type="text"
              placeholder="Search by event or venue..."
              className={styles.searchInput}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
            />
          </div>
                 <button 
                   className={styles.iconBtn} 
                   onClick={handleRefresh}
                   title="Refresh"
                    style={{ fontSize: "20px" }}
                 >
                   
                   <RefreshRoundedIcon />
                 </button>
                 <button 
                   className={styles.iconBtn}
                   style={{ fontSize: "25px" }}
                   onClick={() => setIsDarkMode(!isDarkMode)}
                   title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                 >
                   {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
                 </button>
               </div>
      </div>

 <div className={styles.statsContainer}>
  <div className={styles.statCard}>
    <div className={styles.statNumber}>
      {Number(totalEvents).toLocaleString()}
    </div>
    <div className={styles.statLabel}>Total Events</div>
  </div>

  <div className={`${styles.statCard} ${styles.activeCard}`}>
    <div className={styles.statNumber}>
      {Number(activeEvents).toLocaleString()}
    </div>
    <div className={styles.statLabel}>Active</div>
  </div>

  <div className={`${styles.statCard} ${styles.deactivatedCard}`}>
    <div className={styles.statNumber}>
      {Number(deactivatedEvents).toLocaleString()}
    </div>
    <div className={styles.statLabel}>Removal</div>
  </div>
{/* 
  <div className={`${styles.statCard} ${styles.sportsCard}`}>
    <div className={styles.statNumber}>
      {Number(sportEvents).toLocaleString()}
    </div>
    <div className={styles.statLabel}>Sports Events</div>
  </div>

  <div className={`${styles.statCard} ${styles.musicCard}`}>
    <div className={styles.statNumber}>
      {Number(musicEvents).toLocaleString()}
    </div>
    <div className={styles.statLabel}>Music Events</div>
  </div>

  <div className={`${styles.statCard} ${styles.theaterCard}`}>
    <div className={styles.statNumber}>
      {Number(theaterEvents).toLocaleString()}
    </div>
    <div className={styles.statLabel}>Theater/Comedy</div>
  </div> */}
</div>

      {/* FILTERS */}
      <div className={styles.filterSection}>
        <div className={styles.leftFilters}>
        
          <div className={styles.dropdownWrapper}>
            <input
              type="text"
              placeholder="Search league..."
              className={`${styles.dropdown} ${styles.dropdownInput}`}
              value={leagueSearch}
              onChange={(e) => {
                setLeagueSearch(e.target.value);
                setShowLeagueSuggestions(true);
              }}
              onFocus={() => setShowLeagueSuggestions(true)}
              onBlur={() => setTimeout(() => setShowLeagueSuggestions(false), 150)}
            />
            {showLeagueSuggestions && (
              <div className={styles.suggestionsList} role="listbox">
                {displayedLeagues.map((league: string) => (
                  <div
                    key={league}
                    role="option"
                    className={styles.suggestionItem}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setLeagueFilter(league);
                      setLeagueSearch(league);
                      setArtistsFilter("All Artists");
                      setArtistSearch("");
                      setShowLeagueSuggestions(false);
                    }}
                  >
                    {league}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={styles.dropdownWrapper}>
            <input
              type="text"
              placeholder="Search artist..."
              className={`${styles.dropdown} ${styles.dropdownInput}`}
              value={artistSearch}
              onChange={(e) => {
                const value = e.target.value;
                setArtistSearch(value);
                setShowArtistSuggestions(true);
                const exactMatch = uniqueArtists.find(
                  (item) => item.toLowerCase() === value.trim().toLowerCase()
                );
                if (exactMatch) {
                  setArtistsFilter(exactMatch);
                } else if (!value.trim()) {
                  setArtistsFilter("All Artists");
                }
              }}
              onFocus={() => setShowArtistSuggestions(true)}
              onBlur={() => setTimeout(() => setShowArtistSuggestions(false), 150)}
            />
            {showArtistSuggestions && (
              <div className={styles.suggestionsList} role="listbox">
                {displayedArtists.map((artist: string) => (
                  <div
                    key={artist}
                    role="option"
                    className={styles.suggestionItem}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setArtistsFilter(artist);
                      setArtistSearch(artist);
                      setShowArtistSuggestions(false);
                    }}
                  >
                    {artist}
                  </div>
                ))}
              </div>
            )}
          </div>
          <select className={styles.dropdown} value={genresFilter} onChange={(e) => {
            console.log("🎵 Genre filter changed:", e.target.value);
            setGenresFilter(e.target.value);
          }}>
            {uniqueGenres.map((genre: string) => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </select>

          <div className={styles.statusFilters}>
            <span className={styles.statusLabel}>Status:</span>
            <button
              className={`${styles.statusBtn} ${styles.allStatusBtn} ${statusFilter === "All" ? styles.active : ""}`}
              onClick={() => {
                console.log("📊 Status filter changed to: All");
                setStatusFilter("All");
              }}
            >
              All ({totalEvents})
            </button>
            <button
              className={`${styles.statusBtn} ${styles.activeStatusBtn} ${statusFilter === "Active" ? styles.active : ""}`}
              onClick={() => {
                console.log("📊 Status filter changed to: Active");
                setStatusFilter("Active");
              }}
            >
              Active ({activeEvents})
            </button>
            <button
              className={`${styles.statusBtn} ${styles.deactivatedStatusBtn} ${statusFilter === "Removal" ? styles.active : ""}`}
              onClick={() => {
                console.log("📊 Status filter changed to: Removal");
                setStatusFilter("Removal");
              }}
            >
              Removal ({deactivatedEvents})
            </button>
          </div>
        </div>
      </div>

      {/* BULK ACTION BAR */}
      {selectedEvents.size > 0 && (
        <div className={styles.bulkActionBar}>
          <div className={styles.bulkInfo}>
            <strong>{selectedEvents.size}</strong> event{selectedEvents.size !== 1 ? 's' : ''} selected
            <button onClick={clearSelection} className={styles.clearSelectionBtn}>
              Clear
            </button>
          </div>
          <div className={styles.bulkActions}>
            <button
              onClick={handleBulkActivate}
              className={styles.bulkActivateBtn}
              disabled={events.filter((e: { id: string; status: string | undefined; }) => selectedEvents.has(e.id) && !isEventActiveStatus(e.status)).length === 0}
            >
              <VisibilityOutlinedIcon fontSize="small" />
              Activate Selected ({events.filter((e: { id: string; status: string | undefined; }) => selectedEvents.has(e.id) && !isEventActiveStatus(e.status)).length})
            </button>
            <button
              onClick={handleBulkDeactivate}
              className={styles.bulkDeactivateBtn}
              disabled={events.filter((e: { id: string; status: string | undefined; }) => selectedEvents.has(e.id) && isEventActiveStatus(e.status)).length === 0}
            >
              <VisibilityOffOutlinedIcon fontSize="small" />
              Removal Selected ({events.filter((e: { id: string; status: string | undefined; }) => selectedEvents.has(e.id) && isEventActiveStatus(e.status)).length})
            </button>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className={styles.tableCard}>
        {showInitialSpinner ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
            <CircularProgress />
          </div>
        ) : error ? (
          <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
            Error loading events. Please try again.
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      checked={events.length > 0 && selectedEvents.size === events.length}
                      onChange={handleSelectAll}
                      className={styles.checkbox}
                    />
                  </th>
                  <th>EVENT NAME</th>
                  <th>LEAGUE</th>
                  <th>ARTIST</th>
                  <th>VENUE</th>
                  <th>VENUE ID</th>
                  <th>DATE</th>
                  <th>TIME</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                 </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                      No events found
                    </td>
                   </tr>
                ) : (
                  events.map((event: any) => (
                    <tr key={event.id} className={selectedEvents.has(event.id) ? styles.selectedRow : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedEvents.has(event.id)}
                          onChange={(e) => handleSelectEvent(event.id, e.target.checked)}
                          className={styles.checkbox}
                        />
                      </td>
                      <td 
                        className={styles.eventName}
                        // onClick={() => handleOpenEventDetails(event)}
                        style={{ cursor: 'pointer' }}
                      >
                        {event.event_name}
                      </td>
                      <td>
                        <span className={`${styles.league} ${getLeagueBadgeClass(getEventLeague(event))}`}>
                          {getDisplayValue(getEventLeague(event))}
                        </span>
                      </td>
                      <td>{event.artist}</td>
                      <td className={styles.venue}>{event.venue}</td>
                      <td className={styles.venueId}>{event.venue_id}</td>
                      <td>{formatDateValue(event.date)}</td>
                      <td>{event.time}</td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${
                            isEventActiveStatus(event.status)
                              ? styles.activeBadge 
                              : styles.deactivatedBadge
                          }`}
                        >
                          {getEventStatusLabel(event.status)}
                        </span>
                      </td>
                      <td className={styles.actions}>
                        <button
                          className={
                            isEventActiveStatus(event.status)
                              ? styles.deactivateBtn
                              : styles.activateBtn
                          }
                          title={isEventActiveStatus(event.status) ? 'Deactivate' : 'Activate'}
                          onClick={() => handleDeactivateEvent(event)}
                          style={
                            isEventActiveStatus(event.status)
                              ? undefined
                              : {
                                  background: '#fff',
                                  color: '#16a34a',
                                  border: '2px solid #16a34a',
                                  borderRadius: '12px',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '6px 12px',
                                  cursor: 'pointer',
                                }
                          }
                          disabled={pendingConfirmEvent && pendingConfirmEvent.id === event.id}
                        >
                          {isEventActiveStatus(event.status)
                            ? (<><VisibilityOffOutlinedIcon fontSize="small" /> Removal</>)
                            : (<><VisibilityOutlinedIcon fontSize="small" /> Activate</>)}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className={styles.tableFooter}>
              <div className={styles.bulkFooterActions}>
                {selectedEvents.size > 0 && (
                  <button onClick={handleSelectAllPages} className={styles.selectAllPagesBtn}>
                    Select All {filteredTotal} matching events
                  </button>
                )}
                <span>
                  {loading && data ? "Updating… " : null}
                  Showing {rangeStart}–{rangeEnd} of {filteredTotal} matching
                </span>
              </div>
              <div className={styles.paginationBar}>
                <label className={styles.pageSizeLabel}>
                  Rows per page
                  <select
                    className={styles.pageSizeSelect}
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <span className={styles.pageIndicator}>
                  Page {filteredTotal === 0 ? 0 : page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageNavBtn}
                  disabled={!canPrev}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className={styles.pageNavBtn}
                  disabled={!canNext}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* EVENT DETAILS MODAL */}
      {openModal && selectedEvent && (
        <div className={styles.modalOverlay} onClick={() => setOpenModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <span className={styles.calendarIcon}>📅</span>
                <h2>Event Details</h2>
              </div>
              <button 
                className={styles.closeBtn}
                onClick={() => setOpenModal(false)}
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* EVENT TITLE & STATUS */}
              <div className={styles.eventHeader}>
                <div>
                  <h3 className={styles.eventTitle}>{getDisplayValue(selectedEvent.event_name)}</h3>
                  <p className={getDisplayClassName(getEventLeague(selectedEvent), styles.eventSubtitle)}>{getDisplayValue(getEventLeague(selectedEvent))}</p>
                </div>
                <span className={`${styles.statusBadge} ${
                  isEventActiveStatus(selectedEvent.status)
                    ? styles.activeBadge 
                    : styles.deactivatedBadge
                }`}>
                  {getEventStatusLabel(selectedEvent.status)}
                </span>
              </div>

              {/* EVENT DETAILS GRID */}
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <label>LEAGUE</label>
                  <p className={getDisplayClassName(getEventLeague(selectedEvent))}>{getDisplayValue(getEventLeague(selectedEvent))}</p>
                </div>
                <div className={styles.detailItem}>
                  <label>VENUE</label>
                  <p className={getDisplayClassName(selectedEvent.venue)}>{getDisplayValue(selectedEvent.venue)}</p>
                  <small className={getDisplayClassName(selectedEvent.location)}>{getDisplayValue(selectedEvent.location)}</small>
                </div>
              </div>

              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <label>GENRE</label>
                  <p className={getDisplayClassName(selectedEvent.genre)}>{getDisplayValue(selectedEvent.genre)}</p>
                </div>
                <div className={styles.detailItem}>
                  <label>VENUE ID</label>
                  <p className={getDisplayClassName(selectedEvent.venue_id, styles.venueIdText)}>{getDisplayValue(selectedEvent.venue_id)}</p>
                </div>
              </div>

              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <label>SUB-GENRE</label>
                  <p className={getDisplayClassName(selectedEvent.sub_genre)}>{getDisplayValue(selectedEvent.sub_genre)}</p>
                </div>
                <div className={styles.detailItem}>
                  <label>DATE & TIME</label>
                  <p className={getDisplayClassName(selectedEvent.date)}>{formatDateValue(selectedEvent.date)}</p>
                  <p className={getDisplayClassName(selectedEvent.time)}>{getDisplayValue(selectedEvent.time)}</p>
                </div>
              </div>

              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <label>SEGMENT</label>
                  <p className={getDisplayClassName(selectedEvent.segment)}>{getDisplayValue(selectedEvent.segment)}</p>
                </div>
              </div>

              {/* ADDITIONAL METADATA */}
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>Additional Metadata</h4>
                <div className={styles.metadataGrid}>
                  <div className={styles.metadataItem}>
                    <label>PRICE RANGE</label>
                    <p className={getDisplayClassName(formatPriceRange(selectedEvent.price_range), styles.priceText)}>{formatPriceRange(selectedEvent.price_range)}</p>
                  </div>
                  <div className={styles.metadataItem}>
                    <label>TICKET LIMIT</label>
                    <p className={getDisplayClassName(selectedEvent.ticket_limit)}>{getDisplayValue(selectedEvent.ticket_limit)}</p>
                  </div>
                  <div className={styles.metadataItem}>
                    <label>FAMILY FRIENDLY</label>
                    <p className={getDisplayClassName(selectedEvent.family_friendly)}>{getDisplayValue(selectedEvent.family_friendly)}</p>
                  </div>
                </div>
              </div>

              {/* SALE INFORMATION */}
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>Sale Information</h4>
                <div className={styles.metadataGrid}>
                  <div className={styles.metadataItem}>
                    <label>ON SALE DATE</label>
                    <p className={getDisplayClassName(selectedEvent.on_sale_date)}>{formatDateValue(selectedEvent.on_sale_date)}</p>
                  </div>
                  <div className={styles.metadataItem}>
                    <label>PRESALE DATE</label>
                    <p className={getDisplayClassName(selectedEvent.presale_date)}>{formatDateValue(selectedEvent.presale_date)}</p>
                  </div>
                  <div className={styles.metadataItem}>
                    <label>PROMOTER</label>
                    <p className={getDisplayClassName(selectedEvent.promoter)}>{getDisplayValue(selectedEvent.promoter)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className={styles.modalFooter}>
              <button 
                className={styles.closeModalBtn}
                onClick={() => {
                  console.log("❌ Closing modal");
                  setOpenModal(false);
                }}
              >
                Close
              </button>
              <button
                className={
                  isEventActiveStatus(selectedEvent.status)
                    ? styles.deactivateBtn
                    : styles.activateBtn
                }
                onClick={() => handleDeactivateEvent(selectedEvent)}
                style={
                  isEventActiveStatus(selectedEvent.status)
                    ? {
                        fontSize: '16px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 18px',
                      }
                    : {
                        background: '#fff',
                        color: '#16a34a',
                        border: '2px solid #16a34a',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 18px',
                        cursor: 'pointer',
                      }
                }
              >
                {isEventActiveStatus(selectedEvent.status) ? (
                  <><VisibilityOffOutlinedIcon fontSize="small" /> Deactivate</>
                ) : (
                  <><VisibilityOutlinedIcon fontSize="small" /> Activate</>
                )}
                Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketmasterCatalog;